import {
  ConnectionQuality,
  DisconnectReason,
  LocalTrackPublication,
  Room,
  RoomEvent,
  Track,
  type LocalParticipant,
  type Participant,
  type RemoteParticipant,
  type AudioPreset,
  type RemoteTrack,
  type TrackPublication,
} from "livekit-client";
import {
  audioTuning,
  captureConstraints,
  gateThreshold,
  levelWanted,
  modelWanted,
  platformConstraints,
} from "@shared/media";
import { cue, deviceOf, micWanted, onPushChange, pushOpen, setMicWanted, usePushToTalk } from "@shared/media";
import { holdRoom } from "@shared/media";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api } from "@shared/api";
import type { Uuid } from "@shared/api";
import {
  hasAppSound,
  hasNativeCapture,
  onCaptureStats,
  startAppSound,
  startNativeCapture,
  stopNativeCapture,
  type AppSound,
  type CaptureStats,
} from "@shared/capture";
import { fail, mask, note, warn } from "@shared/lib/journal";
import { assertCanStartRestartSensitiveOperation } from "@shared/lib/desktop";

/**
 * Что именно показывать нативно: монитор или окно.
 *
 * Одним типом, а не двумя необязательными числами: «оба заданы» — состояние
 * бессмысленное, и лучше, чтобы его нельзя было выразить.
 */
export type NativeSource =
  | { kind: "screen"; id: number }
  | { kind: "window"; id: number }
  /**
   * Приложение: то же окно, но снятое просьбой к нему самому.
   *
   * Отдельный вид, а не признак у `window`, потому что различаются они не
   * номером, а способом съёмки, и выбирает человек именно способ.
   */
  | { kind: "app"; id: number };

/**
 * Профиль демонстрации экрана.
 *
 * Главный сценарий — игра, а это худший случай для кодека: движение по всему
 * кадру и почти никакой статики. Пресеты LiveKit сюда не годятся:
 * `screenShareH1080FPS15` — это 3 Мбит/с при 15 кадрах, на игре получится
 * каша из блоков.
 *
 * Частота важнее разрешения. Выбранные кадры держатся даже ценой картинки:
 * 720p60 в игре лучше, чем 1080p30 — размытие в движении и отзывчивость
 * заметнее числа пикселей. Это и означает `maintain-framerate`: под нехваткой
 * полосы падает разрешение, а не частота.
 */
export type ScreenCodec = "h264" | "vp9" | "vp8";

/**
 * Кодек демонстрации.
 *
 * H.264 брали ради аппаратного энкодера — но у браузерного источника его
 * может и не быть: Chrome нередко кодирует демонстрацию программным
 * OpenH264, а это базовый профиль, и на игре он рассыпается на квадраты
 * при любом битрейте. VP9 в браузере кодируется программно всегда, но
 * заметно лучше на бит и на движении. Что окажется лучше на конкретной
 * машине, видно только из показателей — поэтому выбор здесь, а не в
 * константе.
 */
export const SCREEN_CODECS: { id: ScreenCodec; label: string; note: string }[] = [
  { id: "h264", label: "H.264", note: "аппаратный, если браузер его даст" },
  { id: "vp9", label: "VP9", note: "лучше на бит, кодирует процессор" },
  { id: "vp8", label: "VP8", note: "запасной, если первые два не идут" },
];

export interface ScreenProfile {
  height: 1080 | 720 | 480;
  fps: 60 | 30 | 15;
  /**
   * H.264 на игровом контенте: ниже 8 Мбит/с «хорошо» не бывает. Половина
   * кадров стоит примерно половины полосы, четверть пикселей — тоже.
   */
  maxBitrate: number;
}

/*
 * Пары для браузерного показа. Совпадают с лестницей ниже: разойдись они —
 * выбранное в окне и отданное в сеть значили бы разное.
 *
 * Потолки опущены после замера с двух сторон: на 1080p при 20 Мбит/с
 * терялось 3% пакетов, на 720p при 6 Мбит/с — 0.10%. Прежние двадцать пять
 * путь просто не уносил.
 */
export const SCREEN_PROFILES: ScreenProfile[] = [
  { height: 1080, fps: 60, maxBitrate: 10_000_000 },
  { height: 1080, fps: 30, maxBitrate: 6_000_000 },
  { height: 720, fps: 60, maxBitrate: 6_000_000 },
  { height: 720, fps: 30, maxBitrate: 3_500_000 },
];

/*
 * Разрешение и частота — два независимых ряда, а не готовые пары.
 *
 * Пары вида «низкое / высокое» прячут от человека то, что он на самом деле
 * отдаёт; здесь он видит оба числа и меняет их порознь.
 */
export const SCREEN_HEIGHTS = [1080, 720, 480] as const;
export const SCREEN_RATES = [60, 30, 15] as const;

/**
 * Потолок потока для сочетания высоты и частоты.
 *
 * Числа не выведены формулой, а поставлены по замеру. Прежние считались как
 * доля от 25 Мбит/с на 1080p·60, и это оказалось выше того, что путь уносит:
 * на 1080p при 20 Мбит/с теряется 3% пакетов, переспросов 33 в секунду,
 * замирания десять раз за минуту. Тот же показ на 720p·30 при 6 Мбит/с —
 * потери 0.10%, переспросов нет, просадка одна за 45 секунд.
 *
 * Потери упали в тридцать раз вместе с битрейтом: упираемся в объём, а не в
 * кодировщик и не в буферы по дороге. Поэтому лестница поставлена ниже
 * порога и близко к тому, что берут за 1080p·60 в других местах, — там те же
 * 8–10 Мбит/с, а не двадцать пять.
 *
 * Чистая формула здесь и не годилась: доля от площади кадра занижает нижние
 * ступени сильнее, чем нужно, — мелкой картинке в движении запас тоже нужен.
 */
const BITRATES: Record<number, Record<number, number>> = {
  1080: { 60: 10_000_000, 30: 6_000_000, 15: 3_500_000 },
  720: { 60: 6_000_000, 30: 3_500_000, 15: 2_000_000 },
  480: { 60: 3_000_000, 30: 2_000_000, 15: 1_200_000 },
};

export function bitrateFor(height: number, fps: number): number {
  return BITRATES[height]?.[fps] ?? 6_000_000;
}

/** Собирает профиль из двух рядов. */
export function profileOf(height: number, fps: number): ScreenProfile {
  return {
    height: height as ScreenProfile["height"],
    fps: fps as ScreenProfile["fps"],
    maxBitrate: bitrateFor(height, fps),
  };
}

export const DEFAULT_SCREEN_PROFILE = SCREEN_PROFILES[0];

/**
 * Потолок голоса. То же самое, но про звук, и число тут своё.
 *
 * `livekit-client` без указаний берёт `AudioPresets.music` — 48 кбит/с
 * (проверено в его `defaults`, не по памяти). Мало: Opus при нехватке
 * экономит первым делом на верхних частотах, и голос от этого глохнет — то
 * самое «у них ярче», с которого начался разговор о звуке.
 *
 * Шестьдесят четыре — столько же берёт Discord по умолчанию, и для моно
 * этого хватает с запасом: выше него на речи слышно уже не прибавку, а
 * потраченную полосу. Готовым пресетом это не записать — тот, что равен
 * шестидесяти четырём, называется `musicStereo`, а стерео мы не шлём.
 */
const VOICE: AudioPreset = { maxBitrate: 64_000 };

/**
 * Потолок звука показа на Linux. Там дорожку публикуем мы, а не LiveKit, и
 * без указаний она ушла бы теми же 48 кбит/с в моно. Это звук игры или
 * фильма, а не речь: стерео и 128 кбит/с, как `musicHighQualityStereo`.
 */
const SCREEN_SOUND: AudioPreset = { maxBitrate: 128_000 };

/** Ширина выводится из высоты: 16:9 — то, во что играют. */
export function widthOf(profile: ScreenProfile): number {
  return Math.round((profile.height * 16) / 9 / 2) * 2;
}

export function sameProfile(a: ScreenProfile, b: ScreenProfile): boolean {
  return a.height === b.height && a.fps === b.fps;
}

/**
 * Владелец потока по идентичности участника.
 *
 * Нативный источник входит в комнату отдельным участником: взять
 * идентичность человека нельзя — у него уже есть своё подключение с ней, и
 * LiveKit выбьет одно из двух. Поэтому производная `{uuid}:screen`, и здесь
 * она сводится обратно.
 */
export function ownerOf(identity: string): Uuid {
  const screen = identity.endsWith(":screen");
  return screen ? identity.slice(0, -":screen".length) : identity;
}

export function isNativeScreen(identity: string): boolean {
  return identity.endsWith(":screen");
}

/**
 * Можно ли нам сейчас говорить — по правам, которые держит LiveKit.
 *
 * Их ставит сервер: в токене при входе и заново, когда модератор заглушил или
 * снял заглушение. Пустой список источников у LiveKit значит «любые».
 */
function micAllowed(participant: LocalParticipant): boolean {
  const permissions = participant.permissions;
  if (!permissions) return true;

  return (
    permissions.canPublish &&
    (permissions.canPublishSources.length === 0 ||
      permissions.canPublishSources.some((source) => Track.sourceFromProto(source) === Track.Source.Microphone))
  );
}

/**
 * Мы ли это — с поправкой на то, что показ приходит отдельным участником.
 *
 * Нужно ровно для звуковых указаний. Показ из приложения идёт мимо комнаты и
 * появляется в ней как `{кто}:screen` — с нашей же стороны это обычный чужой
 * участник, и о собственном показе мы услышали бы дважды: один раз от
 * `startScreen`, второй от комнаты.
 */
function ours(room: Room, identity: string): boolean {
  return ownerOf(identity) === ownerOf(room.localParticipant.identity);
}

/**
 * Проверяет через десять секунд, дошёл ли показ до комнаты.
 *
 * Нативный показ приходит не той дорогой, какой уходит просьба: страница
 * просит оболочку, та запускает программу, программа стучится в точку приёма
 * WHIP, и уже оттуда в комнате появляется отдельный участник `{кто}:screen`.
 * Всё это может тихо не случиться — и тогда у человека просто ничего не
 * происходит.
 *
 * Отсюда и проверка: она ничего не чинит, она отвечает на вопрос «докуда
 * дошло». Появился участник — дошло до конца, и беда, если она есть, в
 * картинке. Не появился — дальше смотреть надо в строках программы захвата,
 * они в том же журнале и выше.
 *
 * Десять секунд: переговоры WHIP укладываются в одну-две, ожидание связи в
 * программе ограничено пятнадцатью. Меньше — ложная тревога на медленной
 * сети, больше — человек успевает уйти.
 */
function watchForShare(room: Room) {
  window.setTimeout(() => {
    const shown = [...room.remoteParticipants.values()]
      .map((one) => one.identity)
      .filter(isNativeScreen);

    if (shown.length > 0) {
      note("показ", "показ дошёл до комнаты", { участники: shown.join(", ") });
      return;
    }

    warn("показ", "за 10 с показ в комнате не появился", {
      всего: room.remoteParticipants.size,
      подсказка: "смотреть строки области «захват» выше",
    });
  }, 10_000);
}

/**
 * Почему устройств нет — если их нет.
 *
 * Микрофон, камеру и показ экрана браузер отдаёт только в защищённом
 * окружении: https или localhost. По адресу вида `http://192.168.1.57:1420`
 * объекта `navigator.mediaDevices` не существует вовсе — это не отказ в
 * доступе, которого можно добиться разрешением, а отсутствие самого входа.
 *
 * Без этой проверки попытка кончается «Cannot read properties of undefined
 * (reading 'getUserMedia')» — сообщением, по которому не догадаться, что
 * лечится оно сменой адреса в строке браузера, а не настройками.
 */
function noDevices(): string | null {
  if (typeof navigator !== "undefined" && navigator.mediaDevices) return null;

  if (window.isSecureContext) {
    return "Браузер не даёт этой странице доступ к устройствам.";
  }

  const local = `http://localhost${location.port ? `:${location.port}` : ""}`;
  return (
    `Микрофон, камера и показ экрана работают только по https или с localhost, ` +
    `а открыто по ${location.origin}. Откройте ${local} — или desktop-сборку, ` +
    `там показ экрана идёт мимо браузера и адрес ему не важен.`
  );
}

/** Кто и в каком виде сейчас в звонке. */
export interface Peer {
  /** Кому принадлежит: у нативного потока это владелец, а не сама идентичность. */
  id: Uuid;
  /** Идентичность в LiveKit — по ней ищется дорожка. */
  identity: string;
  /** Это мы. */
  self: boolean;
  /** Говорит прямо сейчас. */
  speaking: boolean;
  /** Микрофон включён и не заглушён. */
  microphone: boolean;
  camera: boolean;
  screen: boolean;
}

/**
 * Звуковая дорожка вместе с тем, чья она и что это.
 *
 * Одной дорожки мало: голос и звук трансляции настраиваются по отдельности, а
 * значит надо знать не только «что играть», но и «чьё это» и «голос ли».
 */
export interface AudioSlot {
  publication: TrackPublication;
  /** Чей звук. У трансляции это владелец, а не сама её идентичность. */
  owner: Uuid;
  /** Звук показа экрана, а не голос. */
  screen: boolean;
}

export type CallState = "idle" | "joining" | "live" | "failed";

export interface Call {
  state: CallState;
  /** Канал, в котором идёт звонок. `null` — мы нигде. */
  channelId: Uuid | null;
  peers: Peer[];
  microphone: boolean;
  camera: boolean;
  screen: boolean;
  /** Оглушены: не слышим никого. */
  deafened: boolean;
  problem: string | null;
  /** Из какого канала нас только что перевели. Гаснет само. */
  movedFrom: Uuid | null;
  /** Перевёл модератор, а не мы сами. */
  pushed: boolean;
  /**
   * Как держится наше собственное соединение.
   *
   * Своё, а не чужое: когда картинка сыплется, первый вопрос — у кого. Оценку
   * даёт сервер по потерям и джиттеру, поэтому она честнее догадки по
   * картинке. `null` — пока не сказали.
   */
  quality: ConnectionQuality | null;
  /**
   * Числа под оценкой: задержка до сервера и потери нашего голоса.
   *
   * Оценка `quality` отвечает «к кому вопрос», а эти — «насколько». Врозь,
   * потому что берутся из разных мест: оценку присылает сервер, числа
   * снимаются с WebRTC здесь.
   */
  link: LinkStats | null;
  /** Звуковые дорожки собеседников: их надо во что-то вставить, чтобы слышать. */
  audio: AudioSlot[];

  /** `pushed` — переходим по велению модератора: подпись скажет «вас перенесли». */
  join: (channelId: Uuid, pushed?: boolean) => Promise<void>;
  leave: (signal?: AbortSignal) => Promise<void>;
  toggleMicrophone: () => Promise<void>;
  toggleCamera: () => Promise<void>;
  /**
   * Профиль обязателен: гадать за пользователя, в чём он играет, не из чего.
   * `monitor` — только для нативного пути; браузер источник выбирает сам.
   */
  startScreen: (
    profile: ScreenProfile,
    codec: ScreenCodec,
    source?: NativeSource,
    /** Брать ли звук источника. Только для окна: у монитора процесса нет. */
    sound?: boolean,
    /** Процесс, чей звук показывать, — Linux-оболочка (`shared/capture/sound.ts`). */
    soundFrom?: number,
  ) => Promise<void>;
  stopScreen: (signal?: AbortSignal) => Promise<void>;
  toggleDeafen: () => void;
  /** Дорожка картинки участника: камера или экран. */
  videoOf: (peerId: Uuid, source: "camera" | "screen") => TrackPublication | null;
  /** Что на самом деле происходит с демонстрацией, за которой следим. */
  stats: ScreenStats | null;
  /**
   * За чьей демонстрацией следить. Демонстраций бывает несколько, и
   * показатели должны относиться к той, на которую смотрят, а не к первой
   * попавшейся.
   */
  watchStats: (peerId: Uuid | null) => void;
  /**
   * Чьи трансляции смотрим — как «Смотреть трансляцию» в Discord. Чужой экран
   * качается, только пока его смотрят; свой показ виден всегда.
   */
  watching: ReadonlySet<Uuid>;
  /** Начать или прекратить смотреть трансляцию человека. */
  watchScreen: (peerId: Uuid, on: boolean) => void;
  /** Чьё видео отключено у себя — «Отключить видео» в Discord: камера не качается. */
  camerasOff: ReadonlySet<Uuid>;
  hideCamera: (peerId: Uuid, off: boolean) => void;
  /**
   * Напомнить серверу о себе — послать своё состояние ещё раз. Сервер, который
   * вычеркнул нас из состава, пока мы переподключались, по нему вернёт нас
   * (сверившись с комнатой LiveKit).
   */
  reclaim: () => void;
}

/**
 * Показатели самого голосового соединения.
 *
 * Отдельно от `ScreenStats`: те привязаны к выбранной демонстрации и живут,
 * только пока за ней следят, а эти нужны всегда, пока идёт разговор, — их и
 * спрашивают первыми, когда голос рвётся.
 *
 * Каждое число умеет быть `null`, и это не то же самое, что ноль. Ноль
 * миллисекунд и ноль потерь — утверждения об измерении; `null` значит, что
 * измерения не было. Спутать их значит однажды показать «связь идеальная»
 * там, где на самом деле мерить было нечем.
 */
export interface LinkStats {
  /** Круговая задержка до сервера сейчас, миллисекунды. */
  rttMs: number | null;
  /** Она же в среднем за последнюю минуту: мгновенная скачет, средняя нет. */
  avgRttMs: number | null;
  /**
   * Доля потерянных исходящих пакетов за последнюю минуту, в процентах.
   *
   * Только исходящие и только пока микрофон включён: считает их получатель и
   * присылает отчётом о наших пакетах. Молчащему считать нечего — тогда
   * `null`, а не ноль.
   */
  lossPercent: number | null;
}

/**
 * Показатели демонстрации, снятые с WebRTC.
 *
 * Нужны потому, что заказанное и происходящее расходятся молча: битрейт
 * можно попросить любой, а получить столько, сколько даст энкодер и
 * пропустит сеть. Без этих цифр «плохо выглядит» не отличить от
 * «не тянет процессор» и от «зажала полоса».
 */
export interface ScreenStats {
  /**
   * Смотрим ли мы со стороны отправителя.
   *
   * Не то же самое, что «наша демонстрация». Нативный показ публикует
   * отдельный участник через ingress, а браузер к нему подписан — значит
   * поток наш, но числа у нас только приёмные: кодируем его не мы. Подписи
   * обязаны различать эти два случая, иначе «декодирование» читается как
   * «кодирование» и вводит в заблуждение.
   */
  outbound: boolean;
  /** Наша ли это демонстрация — независимо от того, с какой стороны смотрим. */
  mine: boolean;
  /** Идёт ли поток мимо браузера: снят и закодирован нашей программой. */
  native: boolean;
  /**
   * Что говорит о себе программа захвата.
   *
   * Единственный источник чисел отправителя для нативного показа: браузер
   * его не кодирует и знает только то, что до него доехало.
   */
  capture: CaptureStats | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  /**
   * Что отдаёт сам захват, до кодирования.
   *
   * Отделяет «браузер снимает мало кадров» от «энкодер не успевает их
   * сжать»: если захват даёт тридцать, шестидесяти не будет никогда, и
   * никакой битрейт этого не поправит.
   */
  captureWidth: number | null;
  captureHeight: number | null;
  captureFps: number | null;
  /**
   * Сколько кадров источник в самом деле отдаёт энкодеру.
   *
   * Отличается и от заказанного захвату, и от ушедшего в сеть. Три числа
   * подряд показывают, на каком именно шаге теряются кадры.
   */
  sourceFps: number | null;
  /** Мегабиты в секунду за последнюю секунду. */
  mbps: number | null;
  codec: string | null;
  /**
   * Кто кодирует. Здесь и виден главный подвох: `OpenH264` означает
   * программный энкодер базового профиля — на игре он и даёт квадраты.
   */
  implementation: string | null;
  /** `cpu`, `bandwidth`, `none` — почему качество ограничено прямо сейчас. */
  limitation: string | null;
  /**
   * Доля времени под каждым ограничением, в процентах.
   *
   * Мгновенная причина скачет, а доли — нет: по ним и видно, во что
   * упираемся на самом деле.
   */
  cpuShare: number | null;
  bandwidthShare: number | null;
  /** Замирания у зрителя: сколько раз и сколько всего секунд. */
  freezes: number | null;
  freezeSeconds: number | null;
  /** Доля потерянных пакетов, в процентах. */
  packetLoss: number | null;
  /** Дрожание прихода пакетов, миллисекунды. */
  jitterMs: number | null;
  /** Круговая задержка до сервера, миллисекунды. Только у отправителя. */
  rttMs: number | null;
  /** Сколько миллисекунд уходит на кадр: у отправителя — кодирование, у зрителя — раскодирование. */
  frameMs: number | null;
  /**
   * Насколько грубо сжат кадр: шаг квантования, 0..51 у H.264.
   *
   * Это и есть «квадраты», выраженные числом. Битрейт сам по себе ничего не
   * говорит — двадцать мегабит на спокойной картинке роскошь, а на игре в
   * движении мало; а вот шаг квантования говорит прямо: до тридцати кадр
   * чистый, за сорок сыпется. Считается за последнюю секунду, не за всё
   * время, иначе первые спокойные секунды навсегда занижают число.
   */
  qp: number | null;
  /**
   * Сколько кадров источник отдал, а энкодер не взял, за последнюю секунду.
   *
   * Разница между «источник даёт» и «кодируется». Ненулевая значит, что
   * кадры выбрасываются до кодирования — обычно потому, что энкодер не
   * успевает.
   */
  droppedFrames: number | null;
  /** Сколько кодировщику разрешено, мегабиты в секунду. */
  targetMbps: number | null;
  /**
   * Сколько сеть считает доступным, мегабиты в секунду.
   *
   * Вместе с потоком и целью разделяет два разных «мало битрейта»: упёрлись
   * в свой же потолок или сеть не пропускает.
   */
  availableMbps: number | null;
  /**
   * Ключевых кадров за последнюю секунду.
   *
   * Ключевой кадр в разы тяжелее обычного. Если их несколько в секунду,
   * они съедают поток, и на обычные кадры не остаётся.
   */
  keyFrames: number | null;
  /** Доля потока, ушедшая на переотправку потерянного, в процентах. */
  retransmitShare: number | null;
  /**
   * Сколько пакет ждёт очереди на отправку, миллисекунды.
   *
   * Растёт, когда кодировщик даёт больше, чем сеть уносит. Именно это, а
   * не расстояние до сервера, обычно и раздувает задержку.
   */
  pacingMs: number | null;
  /** Запросов ключевого кадра от зрителя за последнюю секунду: картинка развалилась. */
  keyFrameRequests: number | null;
  /** Переотправок запрошено за последнюю секунду. */
  nacks: number | null;
  /** Сколько раз менялось разрешение из-за нехватки полосы или процессора. */
  resolutionChanges: number | null;
  /** Задержка в буфере зрителя, миллисекунды. */
  bufferMs: number | null;
  /**
   * Последние отсчёты, по одному в секунду.
   *
   * Мгновенное число ничего не говорит: важно, держится оно или скачет.
   * Отсюда и средние, и худшее, и график.
   */
  history: StatSample[];
}

export interface StatSample {
  fps: number | null;
  mbps: number | null;
  qp: number | null;
  /**
   * Ключевых кадров за эту секунду.
   *
   * Хранится в истории, потому что событие редкое: при одном кадре в три
   * секунды мгновенное значение показывает то единицу, то ноль, и по нему
   * не понять ничего. Смысл появляется только у среднего.
   */
  keyFrames: number | null;
}

/**
 * Отчёт getStats.
 *
 * Набор полей зависит от вида отчёта и от браузера, поэтому в привязках он
 * и не описан — каждое поле надо проверять на месте. Свой тип нужен только
 * чтобы отчёт можно было положить в переменную и не потерять.
 */
// Причина `any` — в пояснении выше: набор полей задаёт браузер.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RTCStatsEntry = RTCStats & Record<string, any>;

/**
 * Накопленные счётчики прошлого опроса.
 *
 * Почти всё в getStats растёт с начала передачи, и само по себе такое число
 * бесполезно: среднее за всё время скрывает как раз то, что интересно —
 * что происходит сейчас. Поэтому держим прошлый снимок и показываем разницу.
 */
interface Counters {
  at: number;
  bytes: number;
  packets: number;
  frames: number;
  /** Кадры источника — у отправителя. */
  sourceFrames: number;
  /** Кадры, выброшенные после приёма — у зрителя. */
  dropped: number;
  qpSum: number;
  keyFrames: number;
  retransmitted: number;
  sendDelay: number;
  nacks: number;
  keyFrameRequests: number;
}

/** Разница двух счётчиков, но не отрицательная: счётчики умеют сбрасываться. */
function delta(now: number | undefined, before: number | undefined): number {
  if (now == null || before == null) return 0;
  return Math.max(0, now - before);
}

/**
 * Раскладывает отчёты getStats по видам.
 *
 * Отдельной функцией, а не на месте: порядок обхода не обещан, а почти
 * каждое число собирается из нескольких отчётов сразу — например, доля
 * переотправок берётся из потока, а разрешённый битрейт из связки адресов.
 */
function sortReports(reports: RTCStatsReport, outbound: boolean) {
  const found: Record<"media" | "source" | "remote" | "pair", RTCStatsEntry | null> = {
    media: null,
    source: null,
    remote: null,
    pair: null,
  };

  reports.forEach((report: RTCStatsEntry) => {
    switch (report.type) {
      case "outbound-rtp":
        if (outbound && report.kind === "video") found.media = report;
        break;
      case "inbound-rtp":
        if (!outbound && report.kind === "video") found.media = report;
        break;
      case "media-source":
        if (report.kind === "video") found.source = report;
        break;
      case "remote-inbound-rtp":
        if (report.kind === "video") found.remote = report;
        break;
      case "candidate-pair":
        // Связок несколько, рабочая одна.
        if (report.nominated || report.state === "succeeded") found.pair = report;
        break;
    }
  });

  return found;
}

/** Сколько отсчётов держим: минута при опросе раз в секунду. */
const HISTORY_LENGTH = 60;

function publicationsOf(participant: Participant) {
  const of = (source: Track.Source) => participant.getTrackPublication(source) ?? null;

  return {
    microphone: of(Track.Source.Microphone),
    camera: of(Track.Source.Camera),
    screen: of(Track.Source.ScreenShare),
  };
}

function peerOf(participant: Participant, self: boolean): Peer {
  const tracks = publicationsOf(participant);

  return {
    id: ownerOf(participant.identity),
    identity: participant.identity,
    self,
    speaking: participant.isSpeaking,
    // Отсутствие дорожки и выключенный микрофон для собеседника выглядят
    // одинаково — и то и другое значит, что человека не слышно.
    microphone: !!tracks.microphone && !tracks.microphone.isMuted,
    camera: !!tracks.camera && !tracks.camera.isMuted,
    screen: !!tracks.screen && !tracks.screen.isMuted,
  };
}

/**
 * Звонок: одно подключение к LiveKit на всё приложение.
 *
 * Звонок один — вход в другой канал выводит из прежнего. Это правило есть и
 * на сервере, здесь оно повторено, потому что комнату надо ещё и закрыть:
 * сервер о нашем соединении с LiveKit не знает.
 */
/**
 * Ставит тракт микрофона на только что опубликованную дорожку.
 *
 * После публикации, а не через `audioCaptureDefaults`: обработчик,
 * переданный умолчаниями, LiveKit применяет внутри `createLocalTracks` —
 * а дорожку он там создаёт без звукового потока (`mediaTrackToLocalTrack`
 * передаёт на его место `undefined`) и тут же падает собственной проверкой.
 * Микрофон в итоге не включается вовсе, и ни в вебе, ни в оболочке.
 *
 * К этому мигу поток уже задан: его ставит `createTracks` сразу после
 * создания дорожки и до публикации.
 *
 * Отказ здесь ничего не ломает: не поставился тракт — микрофон работает
 * как есть, со штатной обработкой. Это то же правило, что и внутри самого
 * тракта: микрофон важнее шумодава.
 */
/**
 * Рация, пока тракта нет: тракт встаёт не сразу (модель грузится секунды) и
 * может не встать вовсе, а глушит он усилением — без него в комнату шёл бы
 * открытый микрофон. Глушим на отправителе: `replaceTrack(null)` не трогает сам
 * трек. Выключать трек (`enabled`) нельзя — собранный на нём тракт после
 * включения остаётся немым (проверено на Electron). Встанет тракт — LiveKit
 * сам поставит в отправитель его дорожку, и дальше глушит усиление.
 */
function applyPush(room: Room): void {
  const track = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track;
  if (!track || track.getProcessor() || !track.sender) return;
  void track.sender.replaceTrack(pushOpen() ? track.mediaStreamTrack : null).catch(() => {});
}

async function attachMicChain(publication: LocalTrackPublication): Promise<void> {
  if (publication.source !== Track.Source.Microphone) return;

  const track = publication.audioTrack;
  // Повторная установка означала бы второй граф на ту же дорожку: при смене
  // устройства LiveKit сам зовёт `restart` у поставленного.
  if (!track || track.getProcessor()) return;

  const model = modelWanted();

  /*
   * Куда откатываться, если тракт не собрался, зависит от того, что он
   * заменял. Под моделью платформенное подавление выключено — значит при её
   * отказе его надо вернуть, иначе человек останется вовсе без подавления.
   * А без модели ограничения захвата уже те, о которых просили: возвращать
   * там нечего, и вернуть «на ступень ниже» значило бы включить подавление
   * тому, кто его выключил.
   */
  /*
   * Шумодав приезжает здесь, а не при запуске приложения.
   *
   * Тракт нужен ровно в этот миг — когда микрофон уже взят и его надо
   * обвязать. Обычным импортом он тянул бы за собой всю обвязку в тот кусок,
   * что грузится при открытии страницы, а слушают в приложении чаще, чем
   * говорят.
   */
  const { createMicChain } = await import("@shared/denoiser");

  const processor = createMicChain(model ? platformConstraints() : captureConstraints(), {
    model,
    gain: audioTuning().micVolume,
    clarity: audioTuning().clarity,
    threshold: gateThreshold(),
    level: levelWanted(),
  });

  try {
    await track.setProcessor(processor);
  } catch {
    // Сказать об этом уже сказано: состояние тракта видно в настройках.
  }
}
export function useCall(): Call {
  const roomRef = useRef<Room | null>(null);
  const [channelId, setChannelId] = useState<Uuid | null>(null);
  const [state, setState] = useState<CallState>("idle");
  // Рация слушает клавишу, только пока звонок идёт: вне его микрофону нечего глушить.
  usePushToTalk(state === "live");
  useEffect(
    () =>
      onPushChange(() => {
        if (roomRef.current) applyPush(roomRef.current);
      }),
    [],
  );
  const [peers, setPeers] = useState<Peer[]>([]);
  const [deafened, setDeafened] = useState(false);
  /** То же оглушение — для обработчиков комнаты, которые живут дольше одной отрисовки. */
  const deafRef = useRef(false);
  /** Оглушены на сервере: LiveKit не даёт нам никого слушать. Соседям — как оглушение. */
  const [silenced, setSilenced] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [movedFrom, setMovedFrom] = useState<Uuid | null>(null);
  const [pushed, setPushed] = useState(false);
  const [quality, setQuality] = useState<ConnectionQuality | null>(null);
  /** Растёт, когда своё состояние надо послать серверу заново, хоть оно и не менялось. */
  const [claim, setClaim] = useState(0);
  /** Растёт на каждое событие комнаты: по нему пересобираются дорожки. */
  const [revision, setRevision] = useState(0);
  /** Чьи трансляции смотрим. Ссылка — для обработчиков комнаты: они живут дольше отрисовки. */
  const [watching, setWatching] = useState<ReadonlySet<Uuid>>(() => new Set());
  const watchingRef = useRef(watching);
  watchingRef.current = watching;
  /** Чьё видео отключено у себя. Держится на сеанс, как и выбор трансляций. */
  const [camerasOff, setCamerasOff] = useState<ReadonlySet<Uuid>>(() => new Set());
  const camerasOffRef = useRef(camerasOff);
  camerasOffRef.current = camerasOff;

  /**
   * Подписка на чужие экраны — по тому, что смотрим. Свой показ (нативный
   * идёт отдельным участником, для нас удалённым) — всегда: это превью.
   */
  const applyWatch = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    const me = ownerOf(room.localParticipant.identity);
    for (const participant of room.remoteParticipants.values()) {
      const owner = ownerOf(participant.identity);
      const want = owner === me || watchingRef.current.has(owner);
      for (const publication of participant.trackPublications.values()) {
        // Камера — пока её не отключили у себя.
        if (publication.source === Track.Source.Camera) {
          const seen = !camerasOffRef.current.has(owner);
          if (publication.isDesired !== seen) publication.setSubscribed(seen);
          continue;
        }
        if (
          publication.source !== Track.Source.ScreenShare &&
          publication.source !== Track.Source.ScreenShareAudio
        ) {
          continue;
        }
        /*
         * По желаемому, а не по факту подписки: пока дорожка не пришла,
         * `isSubscribed` ложен, и отписка от только что опубликованного
         * экрана иначе не уходила вовсе — его качали все.
         */
        if (publication.isDesired !== want) publication.setSubscribed(want);
      }
    }
  }, []);
  /**
   * Идёт ли показ прямо сейчас — для звукового указания об остановке.
   *
   * Не состояние, потому что от него ничего не рисуется: то, что показ идёт,
   * экран узнаёт из `peers`, куда попадает и чужой показ тоже. Здесь нужен
   * ответ строго про себя и строго про оба пути разом.
   */
  const sharingRef = useRef(false);
  /**
   * Звук приложения при показе на Linux. Его берёт оболочка и снимаем мы сами:
   * комната видит только дорожку и о захвате за ней не знает.
   */
  const appSoundRef = useRef<AppSound | null>(null);

  const dropAppSound = useCallback(() => {
    appSoundRef.current?.stop();
    appSoundRef.current = null;
  }, []);

  const sync = useCallback(() => {
    const room = roomRef.current;
    if (!room) {
      setPeers([]);
      return;
    }

    const local = peerOf(room.localParticipant, true);
    const remote = [...room.remoteParticipants.values()].map((participant) =>
      peerOf(participant, false),
    );

    /*
     * Нативный поток сливается с плашкой своего владельца. Иначе человек
     * показывался бы дважды: сам и как безымянный участник со своей же
     * демонстрацией.
     */
    const merged: Peer[] = [];

    for (const peer of [local, ...remote]) {
      const known = merged.find((item) => item.id === peer.id);

      if (!known) {
        merged.push(peer);
        continue;
      }

      // Дорожки складываем: у одного участника голос и камера, у второго экран.
      known.screen = known.screen || peer.screen;
      known.camera = known.camera || peer.camera;
      known.microphone = known.microphone || peer.microphone;
      known.speaking = known.speaking || peer.speaking;

      // Идентичность оставляем ту, у которой есть демонстрация: по ней её
      // потом и искать.
      if (peer.screen) known.identity = peer.identity;
    }

    setPeers(merged);
    setRevision((value) => value + 1);
  }, []);

  const detach = useCallback(() => {
    dropAppSound();
    const room = roomRef.current;
    if (!room) return;

    room.removeAllListeners();
    void room.disconnect();
    roomRef.current = null;
    holdRoom(null);
  }, [dropAppSound]);

  /**
   * Снять показ экрана.
   *
   * Стоит выше выхода из звонка намеренно: тот её зовёт, а `const` вперёд
   * себя не смотрит — список зависимостей считается на отрисовке и упал бы
   * на необъявленном имени.
   */
  const stopScreen = useCallback(async (signal?: AbortSignal) => {
    note("показ", "останавливаю показ");

    /*
     * Указание об остановке — только если показ и правда шёл.
     *
     * Эта функция зовётся и вслепую: с неё начинаются выход из звонка и
     * переход между каналами, и там она снимает «оба пути, не выясняя, какой
     * был запущен». Для уборки это верно, для звука — нет: положивший трубку
     * без всякой трансляции услышал бы её остановку.
     *
     * Признак держится здесь, а не выводится из состояния комнаты: показ из
     * приложения идёт мимо неё, и по её дорожкам он не виден вовсе.
     */
    if (sharingRef.current) {
      sharingRef.current = false;
      cue("share-stop");
    }

    /*
     * Останавливаем оба пути, не выясняя, какой был запущен.
     *
     * Выяснять не по чему и не нужно: остановка незапущенного здесь ничего
     * не стоит и ничем не грозит, а вот ошибиться и оставить работать
     * захват — значит держать занятой видеокарту и точку приёма.
     */
    await stopNativeCapture();
    dropAppSound();

    if (channelId) {
      await api.voice.closeIngress(channelId, signal).catch(() => {});
    }

    /*
     * Комната нужна только последнему шагу, и её отсутствие больше не
     * причина не делать первые два.
     *
     * Раньше здесь стоял выход «нет комнаты — нечего останавливать», и он
     * был неверен ровно про тот путь, который труднее всего заметить:
     * показ из приложения идёт мимо комнаты, отдельной программой в
     * отдельное подключение.
     */
    const room = roomRef.current;

    if (room) {
      try {
        await room.localParticipant.setScreenShareEnabled(false);
      } catch {
        // Остановка своей же дорожки не тот случай, о котором стоит сообщать.
      }
    }

    sync();
  }, [sync, channelId, dropAppSound]);

  const leave = useCallback(async (signal?: AbortSignal) => {
    const leaving = channelId;

    /*
     * Звук — сразу, до всякой уборки.
     *
     * Не ради скорости: уборка тут и так мгновенная. Ради того, что дальше
     * идёт `stopScreen`, а он играет своё указание, и порядок «сначала
     * трубка, потом показ» — это то, как человек и распорядился: он положил
     * трубку, а показ снялся следом сам.
     */
    if (leaving) cue("voice-leave");

    /*
     * Показ снимается вместе с трубкой.
     *
     * Дорожку из браузера уборка комнаты сняла бы и сама: `disconnect`
     * по умолчанию останавливает свои дорожки. А вот показ из приложения
     * идёт мимо комнаты — его снимает отдельная программа и отдаёт своим
     * подключением, — и разрыв её не касается вовсе. До этой правки
     * положивший трубку продолжал показывать экран, и узнать об этом было
     * неоткуда: своего окна у показа нет, а собеседников он уже не видел.
     *
     * Не дожидаемся: закрытие точки приёма — сетевой запрос, а трубка
     * должна ложиться сразу. Сам захват снимается первым делом внутри, до
     * всякой сети.
     */
    const stopping = stopScreen(signal);

    detach();
    setChannelId(null);
    setState("idle");
    setPeers([]);
    setProblem(null);

    await stopping;

    if (leaving) {
      try {
        await api.voice.leave(leaving, false, signal);
      } catch {
        // Своё состояние мы уже сбросили; сервер уберёт нас по разрыву.
      }
    }
  }, [channelId, detach, stopScreen]);

  const joinOnce = useCallback(
    async (next: Uuid, byModerator = false) => {
      try {
        assertCanStartRestartSensitiveOperation();
      } catch (error) {
        setProblem(error instanceof Error ? error.message : "Airel уже перезапускается");
        return;
      }

      const previous = channelId;
      let early: Awaited<ReturnType<typeof api.voice.join>> | null = null;
      if (previous) {
        /*
         * Сначала — пустят ли туда. В полный канал не пустят, и терять ради
         * отказа прежний звонок незачем. Из прежнего сервер при этом выводит
         * сам, той же командой, что и по кнопке «выйти».
         */
        try {
          early = await api.voice.join(next);
        } catch (error) {
          setProblem(
            error instanceof ApiError && error.code === "voice_full"
              ? "Канал заполнен"
              : error instanceof Error
                ? error.message
                : "Не удалось подключиться",
          );
          return;
        }

        // Переход — тот же выход, и показ на нём снимается по той же
        // причине: он привязан к прежнему каналу, а не к нам.
        const stopping = stopScreen();

        detach();

        await stopping;
      }

      setChannelId(next);
      setState("joining");
      setProblem(null);

      try {
        const grant = early ?? (await api.voice.join(next));

        /*
         * И то и другое выключено намеренно.
         *
         * `adaptiveStream` просит у публикующего качество под размер
         * элемента, в котором нарисовано видео. Плитка демонстрации — около
         * семисот пикселей, и заказанные 15 Мбит/с не тратятся никогда:
         * подписчик сам просит меньше, картинка рассыпается на квадраты, а
         * настройки публикации выглядят проигнорированными. Для игры решать
         * должен тот, кто показывает, а не размер чужого окна.
         *
         * `dynacast` глушит то, чего никто не смотрит в полном качестве, —
         * при одном слое это ровно та же беда.
         */
        /*
         * Обработка микрофона задаётся здесь и один раз: `livekit-client`
         * иначе подставит свои умолчания, где включено всё, и выключить
         * шумодав будет нечем.
         *
         * А вот обработчика дорожки здесь нет намеренно, хотя место для него
         * в этих же умолчаниях есть. Переданный сюда, он применяется внутри
         * `createLocalTracks` — до того как LiveKit задаст дорожке звуковой
         * поток, — и падает его же проверкой «Audio context needs to be set
         * on LocalAudioTrack». Микрофон при этом не включается вовсе.
         * Поэтому тракт ставится после публикации, см. `attachMicChain`.
         */
        const room = new Room({
          adaptiveStream: false,
          dynacast: false,
          audioCaptureDefaults: {
            ...captureConstraints(),
            ...deviceOf("microphone"),
          },
          /*
           * `dtx` и `red` тут не перечислены намеренно: и то и другое
           * `livekit-client` включает сам, и оба нам нужны. Первое выключает
           * передачу на тишине — с затвором на микрофоне пауза стала
           * настоящей цифровой тишиной, и платить за неё полосой незачем.
           * Второе шлёт голос с избыточностью, чтобы потерянный пакет не
           * пришлось заклеивать выдумкой декодера.
           */
          publishDefaults: { audioPreset: VOICE },
        });
        roomRef.current = room;
        holdRoom(room);

        room
          .on(RoomEvent.ActiveSpeakersChanged, sync)
          /*
           * Чужой приход и уход — звуком.
           *
           * Событие приходит только про тех, кто появился после нас: уже
           * сидящих LiveKit отдаёт списком при подключении, а не событиями.
           * Поэтому вход в канал, где уже пятеро, не значит пять окликов —
           * и отдельной защиты от этого заводить не нужно.
           *
           * Показ из приложения приходит сюда же, а не дорожкой: он идёт
           * мимо комнаты, отдельным участником `{кто}:screen`.
           */
          .on(RoomEvent.ParticipantConnected, (participant: RemoteParticipant) => {
            if (!ours(room, participant.identity)) {
              cue(isNativeScreen(participant.identity) ? "share-start" : "voice-join");
            }
            sync();
          })
          .on(RoomEvent.ParticipantDisconnected, (participant: RemoteParticipant) => {
            if (!ours(room, participant.identity)) {
              cue(isNativeScreen(participant.identity) ? "share-stop" : "voice-leave");
            }
            sync();
          })
          /*
           * Показ из браузера — дорожкой: этот путь идёт через комнату.
           *
           * Участники со своим показом (`{кто}:screen`) отсюда исключены: об
           * их приходе уже сказано выше, а дорожку они публикуют следом, и
           * получилось бы два указания на одно событие.
           *
           * Про уже идущий показ события нет — такие дорожки LiveKit тоже
           * отдаёт списком, — и вошедший в канал, где давно показывают,
           * ничего не услышит. Так и надо: это не событие, а обстановка.
           */
          .on(
            RoomEvent.TrackPublished,
            (publication: TrackPublication, participant: RemoteParticipant) => {
              // Чужой экран — только если его смотрят; без этого он качался бы у всех.
              applyWatch();
              /*
               * Состав — по самой публикации, а не по подписке: несмотримый экран
               * не подписан, и «показывает» узнавали бы только смотрящие.
               */
              sync();
              if (publication.source !== Track.Source.ScreenShare) return;
              if (ours(room, participant.identity)) return;
              if (isNativeScreen(participant.identity)) return;
              cue("share-start");
            },
          )
          .on(
            RoomEvent.TrackUnpublished,
            (publication: TrackPublication, participant: RemoteParticipant) => {
              // Конец показа — тоже: у несмотревшего отписки не бывает.
              sync();
              if (publication.source !== Track.Source.ScreenShare) return;
              if (ours(room, participant.identity)) return;
              if (isNativeScreen(participant.identity)) return;
              cue("share-stop");
            },
          )
          .on(RoomEvent.TrackSubscribed, (_track: RemoteTrack, _pub, _p: RemoteParticipant) => {
            // Подписка по умолчанию могла прийти раньше отказа — сверяем ещё раз.
            applyWatch();
            sync();
          })
          .on(RoomEvent.TrackUnsubscribed, sync)
          .on(RoomEvent.TrackMuted, sync)
          .on(RoomEvent.TrackUnmuted, sync)
          // Смена микрофона перезапускает дорожку и возвращает её в отправитель —
          // рация без тракта должна закрыть её снова.
          .on(RoomEvent.ActiveDeviceChanged, () => applyPush(room))
          .on(RoomEvent.LocalTrackPublished, (publication: LocalTrackPublication, _p: LocalParticipant) => {
            applyPush(room);
            // Тракт не встал — глушить дальше будет отправитель.
            void attachMicChain(publication).then(() => applyPush(room));
            sync();
          })
          .on(RoomEvent.LocalTrackUnpublished, (publication: LocalTrackPublication) => {
            /*
             * Показ сняли не мы: «Остановить» портала или системы. LiveKit
             * снимает тогда только картинку, а звук приложения пришлось бы
             * слушать без неё, — уходит и он.
             */
            const sound = appSoundRef.current;
            if (publication.source === Track.Source.ScreenShare && sound) {
              void room.localParticipant.unpublishTrack(sound.track).catch(() => {});
              dropAppSound();
            }
            sync();
          })
          // Оценка приходит на каждого участника; чужие нас не касаются —
          // за свою картинку отвечает наш собственный канал.
          .on(RoomEvent.ConnectionQualityChanged, (value, participant) => {
            if (participant.identity === room.localParticipant.identity) setQuality(value);
          })
          /*
           * Вернулись в комнату после обрыва — без нового входа, а сервер мог
           * за это время вычеркнуть нас: сверка состава застала комнату без
           * нас. Тогда нас видно и слышно в звонке, а под каналом нас нет.
           * Своё состояние возвращает в состав.
           */
          .on(RoomEvent.Reconnected, () => setClaim((value) => value + 1))
          /*
           * Модератор заглушил или снял заглушение. Отнятую дорожку LiveKit
           * снимает сам; а когда голос вернули, микрофон включается обратно,
           * если человек его не выключал, — как в Discord.
           */
          .on(RoomEvent.ParticipantPermissionsChanged, (_previous, participant) => {
            if (participant !== room.localParticipant) return;
            const local = room.localParticipant;
            setSilenced(!(local.permissions?.canSubscribe ?? true));
            // С выключенным звуком — нет: оглушённый молчит, пока сам не включит звук.
            if (micAllowed(local) && micWanted() && !deafRef.current && !local.isMicrophoneEnabled) {
              void local.setMicrophoneEnabled(true).catch(() => {});
            }
            sync();
          })
          .on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
            /*
             * Сюда попадает только разрыв не по нашей воле: своя уборка
             * (`detach`) снимает всех слушателей до `disconnect`, и это
             * событие до неё уже не доходит. Значит, звук здесь уместен —
             * человека выкинуло из канала, и узнать об этом надо не глазами.
             */
            sharingRef.current = false;
            dropAppSound();
            cue("voice-leave");
            if (reason === DisconnectReason.PARTICIPANT_REMOVED) setProblem("Вас отключили от звонка");
            /*
             * Сервер об обрыве не знает: он записал нас при входе и вычеркнет
             * только сверкой с комнатой, через двадцать секунд, а в переписке
             * всё это время у собеседника звонит звонок к призраку. Говорим
             * сами; не вышло — сверка уберёт, как раньше. Кроме вытеснения
             * второй вкладкой: в звонке теперь она, и выход выкинул бы её.
             */
            if (reason !== DisconnectReason.DUPLICATE_IDENTITY) void api.voice.leave(next).catch(() => {});

            roomRef.current = null;
            holdRoom(null);
            setState("idle");
            setChannelId(null);
            setPeers([]);
            setQuality(null);
          });

        await room.connect(grant.url, grant.token);
        setSilenced(!(room.localParticipant.permissions?.canSubscribe ?? true));
        // Уже идущие показы пришли списком и подписались сами — снимаем несмотримые.
        applyWatch();

        if (previous) {
          setMovedFrom(previous);
          setPushed(byModerator);
          window.setTimeout(() => setMovedFrom(null), 5000);
        }

        setState("live");

        /*
         * Указание — по «встал в канал», а не по нажатию: между ними сеть,
         * выдача пропуска и подключение к комнате, и любое из трёх может не
         * выйти. Звук на нажатии обещал бы то, чего ещё нет.
         *
         * Переход между каналами звучит одним входом, а не выходом и входом:
         * выше по этой же функции стоит своя уборка, мимо `leave`, — и это
         * не случайность, которую надо бы поправить. Человек не клал трубку,
         * он перешёл, и два звука подряд рассказали бы о другом действии.
         */
        cue("voice-join");

        /*
         * Микрофон включается по глобальному выключателю, а не сам собой:
         * вошедший молча должен войти молча, а вошедший с включённым — сразу
         * говорить, не ища кнопку.
         */
        if (micWanted() && !deafRef.current && micAllowed(room.localParticipant)) {
          try {
            await room.localParticipant.setMicrophoneEnabled(true);
          } catch (error) {
            // Устройства может не быть вовсе — тогда мы в канале и молчим, а
            // причина видна там же, где и всегда.
            setProblem(error instanceof Error ? error.message : "Микрофон недоступен");
          }
        }

        sync();
      } catch (error) {
        detach();
        setState("failed");
        setChannelId(null);
        /*
         * Сервер записал нас ещё при выдаче токена — до подключения к комнате.
         * Не подключились — говорим, что вышли: иначе двадцать секунд мы
         * призрак в составе, а в переписке у собеседника звонит звонок, на
         * который никто не ответит. Отказ самого входа (полный канал) сюда
         * же — тогда выходить неоткуда, и запрос просто вернёт «не был».
         */
        void api.voice.leave(next).catch(() => {});
        setProblem(
          error instanceof ApiError && error.code === "voice_full"
            ? "Канал заполнен"
            : error instanceof Error
              ? error.message
              : "Не удалось подключиться",
        );
      }
    },
    [channelId, detach, sync, stopScreen, applyWatch, dropAppSound],
  );

  /** Куда идёт вход прямо сейчас: второй вход туда же (двойное нажатие, «Принять» и «Подключиться» разом) — пропускается. */
  const joiningTo = useRef<Uuid | null>(null);

  const join = useCallback(
    async (next: Uuid, byModerator = false) => {
      if (channelId === next && state === "live") return;
      if (joiningTo.current === next) return;
      joiningTo.current = next;
      try {
        await joinOnce(next, byModerator);
      } finally {
        if (joiningTo.current === next) joiningTo.current = null;
      }
    },
    [channelId, state, joinOnce],
  );


  /**
   * Выключить звук — как в Discord: не слышим никого и молчим сами. Свой выбор
   * микрофона при этом помнится (`micWanted`): включили звук — микрофон
   * вернулся, каким был. Раньше выключенный звук оставлял микрофон открытым —
   * человек думал, что его не слышно, а его слышали.
   */
  const deafen = useCallback(
    async (next: boolean) => {
      deafRef.current = next;
      setDeafened(next);
      const room = roomRef.current;
      if (!room) return;
      const local = room.localParticipant;
      try {
        if (next && local.isMicrophoneEnabled) await local.setMicrophoneEnabled(false);
        if (!next && micWanted() && micAllowed(local) && !local.isMicrophoneEnabled) {
          await local.setMicrophoneEnabled(true);
        }
      } catch (error) {
        setProblem(error instanceof Error ? error.message : "Микрофон недоступен");
      }
      sync();
    },
    [sync],
  );

  /** Переключатели идут через LiveKit: он же следит за правами из токена. */
  const toggle = useCallback(
    async (what: "microphone" | "camera") => {
      const room = roomRef.current;

      /*
       * Микрофон при выключенном звуке — как в Discord: включаются и звук, и
       * микрофон. Говорить, никого не слыша, Discord не даёт.
       */
      if (what === "microphone" && deafRef.current) {
        setMicWanted(true);
        await deafen(false);
        return;
      }

      /*
       * Микрофон переключается и без комнаты: это глобальный выключатель, а не
       * свойство звонка. Нажатое до входа доедет до канала при подключении —
       * там состояние спрашивается заново.
       *
       * Камера — другое дело: её вне звонка некуда включать, показывать
       * картинку самому себе продукт не умеет.
       */
      if (!room) {
        if (what === "microphone") setMicWanted(!micWanted());
        return;
      }

      const blocked = noDevices();
      if (blocked) {
        setProblem(blocked);
        return;
      }

      const me = room.localParticipant;

      /*
       * Заглушённому на сервере включать нечего — LiveKit дорожку не примет.
       * Выключатель всё равно щёлкает: голос вернут — микрофон включится или
       * останется выключенным, как человек и выбрал.
       */
      if (what === "microphone" && !micAllowed(me)) {
        setMicWanted(!micWanted());
        sync();
        return;
      }

      try {
        if (what === "microphone") {
          const next = !me.isMicrophoneEnabled;
          await me.setMicrophoneEnabled(next);
          // Глобальное состояние ведётся за настоящим, а не наоборот: если
          // устройство отказало, запоминать «включён» было бы неправдой.
          setMicWanted(next);
        } else await me.setCameraEnabled(!me.isCameraEnabled);
        setProblem(null);
      } catch (error) {
        // Отказ в доступе к устройству и отказ по правам выглядят для
        // человека одинаково — важно, что не включилось, и почему.
        setProblem(error instanceof Error ? error.message : "Устройство недоступно");
      }

      sync();
    },
    [sync, deafen],
  );

  const startScreen = useCallback(
    async (
      profile: ScreenProfile,
      codec: ScreenCodec,
      source?: NativeSource,
      sound?: boolean,
      soundFrom?: number,
    ) => {
      try {
        assertCanStartRestartSensitiveOperation();
      } catch (error) {
        setProblem(error instanceof Error ? error.message : "Airel уже перезапускается");
        return;
      }
      const room = roomRef.current;

      note("показ", "просят показать экран", {
        высота: profile.height,
        кадров: profile.fps,
        мбит: Math.round(profile.maxBitrate / 1_000_000),
        кодек: codec,
        источник: source ? `${source.kind} ${source.id}` : "не выбран",
        звук: soundFrom ?? !!sound,
        канал: channelId ?? "нет",
        комната: !!room,
      });

      if (!room) {
        warn("показ", "комнаты нет — показывать некуда");
        return;
      }

      /*
       * В приложении показываем экран мимо браузера.
       *
       * Замер на живой игре: захват заказан на 60, кодировщик успевает,
       * полоса свободна, ничего не ограничивает — а источник отдаёт 15
       * кадров. Их просто не снимают, и настройками это не лечится.
       * Нативный путь на том же железе дал 57.
       *
       * Кодек здесь не спрашивается: программа умеет только H.264, а он для
       * игры и нужен — его кодирует видеокарта.
       */
      if (channelId && (await hasNativeCapture())) {
        try {
          note("показ", "открываю точку приёма");
          const ingress = await api.voice.openIngress(channelId);
          note("показ", "точка приёма открыта", { адрес: mask(ingress.url) });

          await startNativeCapture({
            url: ingress.url,
            fps: profile.fps,
            height: profile.height,
            mbps: Math.round(profile.maxBitrate / 1_000_000),
            monitor: source?.kind === "screen" ? source.id : undefined,
            window: source?.kind === "window" ? source.id : undefined,
            app: source?.kind === "app" ? source.id : undefined,
            /*
             * Звук берётся у процесса, которому принадлежит окно, — поэтому
             * у обоих оконных способов и ни у одного экранного. У монитора
             * своего процесса нет, а системный захват взял бы и голоса
             * собеседников.
             */
            sound:
              sound && source && source.kind !== "screen" ? source.id : undefined,
          });

          setProblem(null);
          sharingRef.current = true;
          cue("share-start");
          watchForShare(room);
        } catch (error) {
          fail("показ", "нативный показ не начался", error);

          // Точка приёма могла остаться открытой: если она создалась, а
          // программа не запустилась, висящая точка займёт идентичность и
          // следующая попытка упрётся в неё.
          await api.voice
            .closeIngress(channelId)
            .then(() => note("показ", "точка приёма закрыта после отказа"))
            .catch((problem: unknown) => fail("показ", "точку приёма не закрыть", problem));

          setProblem(error instanceof Error ? error.message : "Показать экран не вышло");
        }

        // Участник появится сам, отдельным подключением: sync его подхватит
        // по событию комнаты, ждать здесь нечего.
        return;
      }

      note("показ", "иду через браузер: нативного захвата нет");

      /*
       * Проверка стоит после нативной ветки, а не до неё: программа захвата
       * снимает экран сама, мимо браузера, и запрет на устройства её не
       * касается. В приложении показ работает по любому адресу.
       */
      const blocked = noDevices();
      if (blocked) {
        warn("показ", "браузер не пустит к устройствам", { причина: blocked });
        setProblem(blocked);
        return;
      }

      /*
       * Звук на Linux берёт оболочка, у приложения (`startAppSound` ниже), а
       * у браузера его не просим вовсе. Иначе Chromium пишет монитор
       * звуковой карты, и его АРУ опускает громкость монитора до −66 дБ.
       */
      const ownSound = await hasAppSound();

      try {
        const publication = await room.localParticipant.setScreenShareEnabled(
          true,
          {
            /*
             * Без обработки голоса: это звук вкладки или игры, а не речь.
             * С ней Chromium сводил его в моно и пропускал через шумодав и
             * АРУ — музыка выходила глухой и плавала по громкости.
             */
            audio: ownSound ? false : { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
            /*
             * Это потолок, а не обещание. Окно захвата спрашивает только,
             * что показывать; во что оно уложится, зависит от источника —
             * с монитора 4K браузер ужмёт до просимого, с окна 720p ничего
             * не дорисует.
             */
            resolution: { width: widthOf(profile), height: profile.height, frameRate: profile.fps },
          },
          {
            videoCodec: codec,
            // Слоёв нет: они делят общий битрейт и утраивают работу
            // энкодера. Для одного-двух зрителей это чистый убыток, а
            // временные слои в H.264 внутри libwebrtc всё равно слабы.
            simulcast: false,
            degradationPreference: "maintain-framerate",
            /*
             * Именно `screenShareEncoding`, а не `videoEncoding`.
             *
             * Второе относится к камере, а демонстрация берёт первое — и,
             * не найдя его, подставляет свой пресет: 1080p, 15 кадров,
             * 2.5 Мбит/с. Ровно это и получалось, причём молча: заказ
             * уходил в никуда, `qualityLimitationReason` показывал `none`
             * (никто не мешает — так и задано), а картинка на игре
             * рассыпалась на квадраты и шла в пятнадцати кадрах.
             */
            screenShareEncoding: {
              maxBitrate: profile.maxBitrate,
              maxFramerate: profile.fps,
              priority: "high",
            },
          },
        );

        // LiveKit сам ставит демонстрации подсказку `detail` — она про
        // текст и таблицы и тянет энкодер в сторону чёткости пикселей.
        // Игре нужно обратное.
        const source = publication?.track?.mediaStreamTrack;
        if (source) source.contentHint = "motion";

        note("показ", "браузер отдал дорожку", {
          дорожка: publication?.trackSid ?? "нет",
          размер: source ? `${source.getSettings().width}×${source.getSettings().height}` : "?",
          звук: room.localParticipant.getTrackPublication(Track.Source.ScreenShareAudio) ? "есть" : "нет",
        });

        setProblem(null);

        /*
         * Звук приложения — после картинки: до неё человек мог закрыть окно
         * выбора, и звук остался бы без показа. Не взялся звук — показ идёт
         * без него, а человек узнаёт почему.
         */
        if (ownSound && soundFrom != null && publication) {
          try {
            const appSound = await startAppSound(soundFrom);
            appSoundRef.current = appSound;
            await room.localParticipant.publishTrack(appSound.track, {
              source: Track.Source.ScreenShareAudio,
              audioPreset: SCREEN_SOUND,
              forceStereo: true,
              // Тишина в игре — не пауза в речи: прерывистая передача и
              // запасные копии кадров здесь только мешают.
              dtx: false,
              red: false,
            });
            note("показ", "звук приложения опубликован", { процесс: soundFrom });
          } catch (error) {
            dropAppSound();
            fail("показ", "звук приложения не взялся", error);
            setProblem(`Показ идёт без звука: ${error instanceof Error ? error.message : "звук не взялся"}`);
          }
        }

        /*
         * Звук после того, как дорожка получена, а не по нажатию кнопки:
         * между ними стоит окно выбора экрана, и человек может его закрыть.
         * Отменённый показ не должен звучать начавшимся.
         */
        sharingRef.current = true;
        cue("share-start");
      } catch (error) {
        fail("показ", "браузер отказал в показе", error);
        setProblem(error instanceof Error ? error.message : "Показать экран не вышло");
      }

      sync();
    },
    [sync, channelId, dropAppSound],
  );

  /*
   * Оглушение — местное: звук глушится у себя, а не отменой подписки.
   * Отписка освободила бы канал, но возвращаться из неё дольше, а обещание
   * «не слышу» и так выполнено.
   */
  useEffect(() => {
    const room = roomRef.current;
    if (!room) return;

    for (const participant of room.remoteParticipants.values()) {
      for (const publication of participant.trackPublications.values()) {
        if (publication.kind === Track.Kind.Audio) {
          publication.track?.setMuted?.(deafened);
        }
      }
    }
  }, [deafened, revision]);

  /*
   * Уборка при уходе экрана: выход из учётной записи, закрытие вкладки.
   *
   * Захват снимается отдельной строкой, а не через `stopScreen`: та закрывает
   * ещё и точку приёма, а это сетевой запрос — здесь его некому дождаться.
   * Программу же снять надо непременно: приложение остаётся работать, и
   * никем не остановленная она продолжила бы показывать экран.
   */
  useEffect(
    () => () => {
      detach();
      void stopNativeCapture();
    },
    [detach],
  );

  const videoOf = useCallback(
    (peerId: Uuid, source: "camera" | "screen"): TrackPublication | null => {
      const room = roomRef.current;
      if (!room) return null;

      // Ищем по владельцу, а не по точному совпадению: демонстрация может
      // идти от производной идентичности.
      const candidates = [room.localParticipant, ...room.remoteParticipants.values()];
      const participant =
        candidates.find(
          (item) => ownerOf(item.identity) === peerId && publicationsOf(item)[source],
        ) ?? candidates.find((item) => ownerOf(item.identity) === peerId);

      if (!participant) return null;

      const publication = publicationsOf(participant)[source];
      return publication && !publication.isMuted ? publication : null;
    },
    /*
     * `revision` меняется на каждое событие комнаты: без него ссылка на
     * функцию застыла бы и картинка не появлялась до следующей отрисовки.
     * Линтер считает его лишним — он и не входит в тело, но входит в ответ:
     * дорожки берутся из живого объекта комнаты, о котором линтер не знает.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [revision],
  );

  const [stats, setStats] = useState<ScreenStats | null>(null);
  /*
   * Отчёты захвата приходят сами по себе, раз в секунду, и не совпадают по
   * времени с опросом WebRTC. Держим последний в ссылке, а не в состоянии:
   * перерисовывать панель дважды в секунду ради этого незачем.
   */
  const captureRef = useRef<CaptureStats | null>(null);

  useEffect(() => {
    let stop: (() => void) | null = null;

    void onCaptureStats((next) => {
      captureRef.current = next;
    }).then((unlisten) => {
      stop = unlisten;
    });

    return () => stop?.();
  }, []);
  const [watched, setWatched] = useState<Uuid | null>(null);

  /*
   * Опрос раз в секунду: битрейт считается разностью, и чаще его всё равно
   * не измерить. Идёт только пока за кем-то следят — впустую дёргать
   * getStats незачем.
   */
  useEffect(() => {
    const sharing = peers.find((peer) => peer.screen && peer.id === watched);
    if (!sharing) {
      setStats(null);
      return;
    }

    let previous: Counters | null = null;
    let history: StatSample[] = [];
    let alive = true;

    const tick = async () => {
      const room = roomRef.current;
      if (!room) return;

      // Нативный поток идёт от отдельного участника, и наш он или нет,
      // видно по владельцу, а не по тому, кто его публикует.
      const participant = [
        room.localParticipant,
        ...room.remoteParticipants.values(),
      ].find((item) => item.identity === sharing.identity);

      const outbound = participant === room.localParticipant;

      const publication = participant?.getTrackPublication(Track.Source.ScreenShare);
      const track = publication?.track;
      if (!track) return;

      const reports = await (outbound
        ? (track as { sender?: RTCRtpSender }).sender?.getStats()
        : (track as { receiver?: RTCRtpReceiver }).receiver?.getStats());
      if (!reports || !alive) return;

      const settings = (track.mediaStreamTrack?.getSettings?.() ?? {}) as MediaTrackSettings;

      /*
       * Отчёты разбираем в два захода: сперва раскладываем по видам, потом
       * считаем. Порядок обхода getStats не обещан, а почти каждое число
       * здесь получается из нескольких отчётов сразу — например, доля
       * переотправок берётся из потока, а разрешённый битрейт из связки
       * адресов.
       */
      const { media, source, remote, pair } = sortReports(reports, outbound);

      if (!media) return;

      const rtp = media;

      const next: ScreenStats = {
        outbound,
        mine: sharing.self,
        native: isNativeScreen(sharing.identity),
        capture: captureRef.current,
        width: rtp.frameWidth ?? null,
        height: rtp.frameHeight ?? null,
        fps: rtp.framesPerSecond ?? null,
        mbps: null,
        captureWidth: outbound ? (settings.width ?? null) : null,
        captureHeight: outbound ? (settings.height ?? null) : null,
        captureFps: outbound ? (settings.frameRate ?? null) : null,
        sourceFps: source?.framesPerSecond ?? null,
        codec: null,
        implementation: rtp.encoderImplementation ?? rtp.decoderImplementation ?? null,
        limitation: rtp.qualityLimitationReason ?? null,
        cpuShare: null, bandwidthShare: null,
        freezes: rtp.freezeCount ?? null,
        freezeSeconds: rtp.totalFreezesDuration ?? null,
        packetLoss: null, jitterMs: null, rttMs: null, frameMs: null,
        qp: null, droppedFrames: null,
        targetMbps: rtp.targetBitrate != null ? rtp.targetBitrate / 1_000_000 : null,
        /*
         * Оценка полосы есть только у отправителя.
         *
         * `availableOutgoingBitrate` — это сколько мы можем отдать, и у
         * принимаемого потока оно к делу не относится: там показывалась наша
         * же отдача, которой почти нет, и строка читалась как «сеть тянет
         * 0.3 Мбит/с» под потоком в 24.
         */
        availableMbps:
          outbound && pair?.availableOutgoingBitrate != null
            ? pair.availableOutgoingBitrate / 1_000_000
            : null,
        keyFrames: null, retransmitShare: null, pacingMs: null,
        keyFrameRequests: null, nacks: null,
        resolutionChanges: rtp.qualityLimitationResolutionChanges ?? null,
        bufferMs: null,
        history: [],
      };

      if (rtp.jitter != null) next.jitterMs = rtp.jitter * 1000;

      // Сколько машина тратит на кадр. У отправителя это кодирование, у
      // зрителя — раскодирование; и то и другое показывает, не он ли
      // узкое место.
      const frameTime = outbound ? rtp.totalEncodeTime : rtp.totalDecodeTime;
      const frameCount = outbound ? rtp.framesEncoded : rtp.framesDecoded;

      if (frameTime != null && frameCount) {
        next.frameMs = (frameTime / frameCount) * 1000;
      }

      // Потери. У зрителя они прямо здесь, у отправителя приходят обратным
      // отчётом — его разбираем ниже.
      if (!outbound && rtp.packetsReceived) {
        const lost = rtp.packetsLost ?? 0;
        next.packetLoss = (lost / (lost + rtp.packetsReceived)) * 100;
      }

      // Сколько кадр ждёт в буфере, прежде чем его покажут. Буфер растёт от
      // неровного прихода пакетов и добавляется прямо к задержке.
      if (!outbound && rtp.jitterBufferEmittedCount) {
        next.bufferMs = (rtp.jitterBufferDelay / rtp.jitterBufferEmittedCount) * 1000;
      }

      const durations = rtp.qualityLimitationDurations as Record<string, number> | undefined;

      if (durations) {
        const total = Object.values(durations).reduce((sum, value) => sum + value, 0);
        if (total > 0) {
          next.cpuShare = ((durations.cpu ?? 0) / total) * 100;
          next.bandwidthShare = ((durations.bandwidth ?? 0) / total) * 100;
        }
      }

      // Всё, что растёт с начала передачи, показываем разницей за секунду:
      // среднее за всё время скрывает ровно то, что интересно.
      const now: Counters = {
        at: rtp.timestamp as number,
        bytes: (outbound ? rtp.bytesSent : rtp.bytesReceived) ?? 0,
        packets: (outbound ? rtp.packetsSent : rtp.packetsReceived) ?? 0,
        frames: (outbound ? rtp.framesEncoded : rtp.framesDecoded) ?? 0,
        sourceFrames: source?.frames ?? 0,
        dropped: rtp.framesDropped ?? 0,
        qpSum: rtp.qpSum ?? 0,
        keyFrames: (outbound ? rtp.keyFramesEncoded : rtp.keyFramesDecoded) ?? 0,
        retransmitted: rtp.retransmittedBytesSent ?? 0,
        sendDelay: rtp.totalPacketSendDelay ?? 0,
        nacks: rtp.nackCount ?? 0,
        keyFrameRequests: rtp.pliCount ?? 0,
      };

      if (previous && now.at > previous.at) {
        const seconds = (now.at - previous.at) / 1000;
        const bytes = delta(now.bytes, previous.bytes);
        const frames = delta(now.frames, previous.frames);

        next.mbps = (bytes * 8) / seconds / 1_000_000;

        // Шаг квантования — сумма по кадрам, поэтому делим на кадры, а не
        // на время.
        /*
         * Ноль — это «не сообщили», а не «идеально сжато».
         *
         * У принимаемого H.264 браузер `qpSum` не считает вовсе, и нулевая
         * сумма превращалась в шаг квантования 0, то есть в подпись
         * «отлично» над отсутствующими данными. Отличать одно от другого
         * приходится по самой сумме: настоящий шаг нулевым не бывает.
         */
        const qpSum = delta(now.qpSum, previous.qpSum);

        if (frames > 0 && qpSum > 0) {
          next.qp = qpSum / frames;
        }

        if (outbound) {
          // Что источник отдал, но энкодер не взял.
          next.droppedFrames = Math.max(
            0,
            delta(now.sourceFrames, previous.sourceFrames) - frames,
          );
        } else {
          next.droppedFrames = delta(now.dropped, previous.dropped);
        }

        next.keyFrames = delta(now.keyFrames, previous.keyFrames) / seconds;
        next.nacks = delta(now.nacks, previous.nacks) / seconds;
        next.keyFrameRequests = delta(now.keyFrameRequests, previous.keyFrameRequests) / seconds;

        if (bytes > 0) {
          next.retransmitShare =
            (delta(now.retransmitted, previous.retransmitted) / bytes) * 100;
        }

        // Задержка отправки копится по пакетам — на них и делим.
        const packets = delta(now.packets, previous.packets);
        if (packets > 0) {
          next.pacingMs = (delta(now.sendDelay, previous.sendDelay) / packets) * 1000;
        }
      }

      previous = now;

      // Отправителю о потерях сообщает получатель — своих у него нет.
      if (outbound && remote) {
        /*
         * `fractionLost` — доля за последний отчёт, готовая. Считать её
         * самому не из чего: `packetsReceived` в обратном отчёте нет, и
         * деление потерянных на потерянные даёт ровно сто процентов
         * всегда. Именно это и показывалось.
         */
        if (remote.fractionLost != null) {
          next.packetLoss = remote.fractionLost * 100;
        } else if (remote.packetsLost != null && now.packets > 0) {
          next.packetLoss = (remote.packetsLost / now.packets) * 100;
        }

        if (remote.jitter != null) next.jitterMs = remote.jitter * 1000;
        if (remote.roundTripTime != null) next.rttMs = remote.roundTripTime * 1000;
      }

      if (rtp.codecId) {
        const codec = reports.get(rtp.codecId) as { mimeType?: string } | undefined;
        next.codec = codec?.mimeType?.replace("video/", "") ?? null;
      }

      // История копится здесь, а не в отрисовке: она не должна пропадать
      // от перерисовки и не должна расти без предела.
      history = [
        ...history,
        { fps: next.fps, mbps: next.mbps, qp: next.qp, keyFrames: next.keyFrames },
      ].slice(-HISTORY_LENGTH);
      next.history = history;

      setStats(next);
    };

    void tick();
    const timer = window.setInterval(() => void tick(), 1000);

    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [peers, watched]);

  const [link, setLink] = useState<LinkStats | null>(null);

  /*
   * Показатели связи, пока идёт разговор.
   *
   * Спрашиваются у дорожки, а не у комнаты: публичного способа снять числа со
   * всего соединения у LiveKit нет — `room.engine` помечен внутренним, и
   * держаться за него значит ждать поломки на следующем обновлении. Дорожка
   * же отдаёт отчёт сама, и отчёт этот полный: в нём и связка адресов.
   *
   * `getSenderStats` у той же дорожки не годится, хотя названием обещает
   * ровно это: задержку и потери она читает из `outbound-rtp`, где их нет по
   * спецификации, — и всегда возвращает пустоту. Числа эти приходят отчётом
   * получателя (`remote-inbound-rtp`), поэтому отчёт разбирается здесь.
   *
   * Свой микрофон — первый источник, чужая дорожка — запасной. Разница между
   * ними одна: молча слушающему потери исходящих считать не из чего, и тогда
   * их нет вовсе, а не ноль.
   */
  useEffect(() => {
    if (state !== "live") {
      setLink(null);
      return;
    }

    /** Прошлые счётчики: в отчёте они растут с начала звонка. */
    let previous: { lost: number; sent: number } | null = null;
    let history: { rttMs: number | null; lost: number; sent: number }[] = [];
    let alive = true;

    const tick = async () => {
      const room = roomRef.current;
      if (!room) return;

      const mine = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track;

      const heard = mine
        ? null
        : [...room.remoteParticipants.values()]
            .flatMap((participant) => [...participant.trackPublications.values()])
            .find(
              (publication) =>
                publication.kind === Track.Kind.Audio &&
                publication.source === Track.Source.Microphone &&
                publication.track,
            )?.track;

      const reports = await (mine ?? heard)?.getRTCStatsReport();
      if (!reports || !alive) return;

      let rttMs: number | null = null;
      let pairRttMs: number | null = null;
      let lost: number | undefined;
      let sent: number | undefined;

      reports.forEach((report: RTCStatsEntry) => {
        switch (report.type) {
          case "remote-inbound-rtp":
            if (report.kind !== "audio") break;
            if (typeof report.roundTripTime === "number") rttMs = report.roundTripTime * 1000;
            lost = report.packetsLost;
            break;
          case "outbound-rtp":
            if (report.kind === "audio") sent = report.packetsSent;
            break;
          case "candidate-pair":
            // Связок несколько, рабочая одна.
            if (!report.nominated && report.state !== "succeeded") break;
            if (typeof report.currentRoundTripTime === "number") {
              pairRttMs = report.currentRoundTripTime * 1000;
            }
            break;
        }
      });

      const now = rttMs ?? pairRttMs;

      const step =
        previous && lost != null && sent != null
          ? { lost: delta(lost, previous.lost), sent: delta(sent, previous.sent) }
          : { lost: 0, sent: 0 };

      previous = lost != null && sent != null ? { lost, sent } : previous;

      history = [...history, { rttMs: now, ...step }].slice(-HISTORY_LENGTH);

      const measured = history.map((item) => item.rttMs).filter((item): item is number => item != null);
      const sentTotal = history.reduce((sum, item) => sum + item.sent, 0);
      const lostTotal = history.reduce((sum, item) => sum + item.lost, 0);

      setLink({
        rttMs: now,
        avgRttMs: measured.length
          ? measured.reduce((sum, item) => sum + item, 0) / measured.length
          : null,
        // Ничего не отправляли — и терять было нечего.
        lossPercent: sentTotal > 0 ? (lostTotal / (sentTotal + lostTotal)) * 100 : null,
      });
    };

    void tick();
    const timer = window.setInterval(() => void tick(), 1000);

    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [state]);

  const me = peers.find((peer) => peer.self);

  /*
   * Звук собирается списком, а не берётся из плашек: плашку можно прокрутить
   * за край, и голос вместе с ней бы пропал.
   */
  const audio = useMemo(() => {
    const room = roomRef.current;
    if (!room) return [];

    const tracks: AudioSlot[] = [];

    for (const participant of room.remoteParticipants.values()) {
      /*
       * Свой же показ не слушаем.
       *
       * Нативный поток публикует отдельный участник, и для нас он удалённый — то
       * есть «удалённый». Без этой проверки показывающий слышал бы звук
       * своей игры дважды: из самой игры и из потока, с задержкой.
       */
      if (
        isNativeScreen(participant.identity) &&
        ownerOf(participant.identity) === room.localParticipant.identity
      ) {
        continue;
      }

      for (const publication of participant.trackPublications.values()) {
        if (publication.kind === Track.Kind.Audio && publication.track) {
          tracks.push({
            publication,
            owner: ownerOf(participant.identity),
            // По источнику, а не по идентичности участника: звук показа
            // помечается при создании точки приёма именно так.
            screen: publication.source === Track.Source.ScreenShareAudio,
          });
        }
      }
    }

    return tracks;
    /*
     * Пересобирается на каждое событие комнаты — дорожки приходят и уходят.
     * Линтер считает `revision` лишним: в теле его нет, зато есть живой объект
     * комнаты, о котором он не знает, — а меняется тот молча.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  // Выбор смотреть — сразу в подписку.
  useEffect(() => applyWatch(), [watching, camerasOff, applyWatch]);

  /*
   * Кончился показ — кончился и просмотр, как в Discord: новый показ того же
   * человека снова ждёт «Смотреть». Вышли из звонка — не смотрим ничего.
   */
  useEffect(() => {
    if (watching.size === 0) return;
    const live = new Set(state === "live" ? peers.filter((peer) => peer.screen).map((peer) => peer.id) : []);
    if ([...watching].some((id) => !live.has(id))) {
      setWatching(new Set([...watching].filter((id) => live.has(id))));
    }
  }, [peers, state, watching]);

  const hideCamera = useCallback((peerId: Uuid, off: boolean) => {
    setCamerasOff((was) => {
      if (was.has(peerId) === off) return was;
      const next = new Set(was);
      if (off) next.add(peerId);
      else next.delete(peerId);
      return next;
    });
  }, []);

  const watchScreen = useCallback((peerId: Uuid, on: boolean) => {
    setWatching((was) => {
      if (was.has(peerId) === on) return was;
      const next = new Set(was);
      if (on) next.add(peerId);
      else next.delete(peerId);
      return next;
    });
  }, []);

  /*
   * Своё состояние — наружу, тем, кто просто видит канал в списке.
   *
   * Отдельным эффектом, а не строкой в каждом переключателе, по той же
   * причине, по какой микрофон гаснет не только от кнопки: его снимает
   * переход между каналами, отказ устройства и чужая команда «заглушить». В
   * список под каналом должно попадать то, как есть, а не то, что нажали.
   *
   * Отказ звонку не мешает: это подпись под составом, а не сам разговор, — но
   * и молчать о нём нельзя. Пока отказ глотался, «микрофон у всех выключен»
   * выглядело как поломка разметки, а на деле сервер отвечал 404: работал
   * старый бинарь, без этой ручки. Строка в журнале отвечает на это за секунду.
   */
  const microphone = me?.microphone ?? false;
  const screen = me?.screen ?? false;
  const reclaim = useCallback(() => setClaim((value) => value + 1), []);

  useEffect(() => {
    if (state !== "live" || !channelId) return;

    void api.voice
      .state(channelId, { microphone, deafened: deafened || silenced, live: screen })
      .catch((error: unknown) => fail("звонок", "состояние не ушло на сервер", error));
  }, [state, channelId, microphone, deafened, silenced, screen, claim]);

  /*
   * Уходя со страницы — выходим из звонка.
   *
   * Перезагрузка, закрытая вкладка, закрытое окно приложения: комната у
   * LiveKit при этом рвётся сразу, а наш учёт об этом не узнаёт ниоткуда.
   * Признак «человек ушёл совсем» тут не работает — при перезагрузке новая
   * страница успевает подключиться раньше, чем отвалится прежняя, и для
   * учёта человек никуда не уходил. В канале оставался призрак.
   *
   * `pagehide`, а не `beforeunload`: тот в мобильных браузерах не
   * срабатывает вовсе, а этот приходит и при закрытии, и при уходе на
   * другую страницу. Запрос помечен `keepalive` — иначе браузер оборвёт его
   * вместе со вкладкой.
   *
   * Обещания это не даёт: убитый процесс и оборванная сеть по-прежнему
   * оставляют призрака. Их снимает сверка состава с комнатой на стороне
   * сервера — эта строка лишь делает уборку мгновенной там, где она вообще
   * возможна.
   */
  useEffect(() => {
    if (state !== "live" || !channelId) return;

    const drop = () => void api.voice.leave(channelId, true).catch(() => {});

    window.addEventListener("pagehide", drop);
    return () => window.removeEventListener("pagehide", drop);
  }, [state, channelId]);

  return useMemo<Call>(
    () => ({
      state,
      channelId,
      peers,
      microphone,
      camera: me?.camera ?? false,
      screen,
      deafened,
      problem,
      movedFrom,
      pushed,
      quality,
      link,
      audio,
      join,
      leave,
      toggleMicrophone: () => toggle("microphone"),
      toggleCamera: () => toggle("camera"),
      startScreen,
      stopScreen,
      stats,
      watchStats: setWatched,
      toggleDeafen: () => void deafen(!deafRef.current),
      videoOf,
      watching,
      watchScreen,
      camerasOff,
      hideCamera,
      reclaim,
    }),
    [
      state, channelId, peers, me, microphone, screen, deafened, problem, movedFrom, pushed,
      quality, link, audio,
      join, leave, toggle, deafen, startScreen, stopScreen, videoOf, stats, watching, watchScreen,
      camerasOff, hideCamera, reclaim,
    ],
  );
}
